// The unsubscribe ladder, pure parts: where a sender stands in words, what each rung is called,
// and which rungs remain. Every rung is started by the owner (docs/design/unsubscribe.md); the
// wording keeps to CLAUDE.md invariant 8 (calm, no shame).
import type {
  SenderView,
  UnsubscribeMethodsView,
  UnsubscribeRung,
  UnsubscribeStatus,
  UnsubscribeView,
} from "@shared/api";

export const RUNGS: readonly UnsubscribeRung[] = ["one_click", "mailto", "link"];

/** The button label of a rung: what the owner would be starting. */
export function rungLabel(rung: UnsubscribeRung): string {
  switch (rung) {
    case "one_click":
      return "Unsubscribe (one-click)";
    case "mailto":
      return "Unsubscribe by email";
    case "link":
      return "Open their unsubscribe page";
  }
}

/** One line on what a rung does, for the button's title and the panel. */
export function rungHint(rung: UnsubscribeRung, methods: UnsubscribeMethodsView): string {
  switch (rung) {
    case "one_click":
      return `MeeMail sends one unsubscribe request to ${methods.oneClick?.host ?? "their server"}, after you confirm the exact address.`;
    case "mailto":
      return `Opens a draft to ${methods.mailto?.address ?? "their unsubscribe address"}; you send it like any message.`;
    case "link":
      return `Opens ${methods.link?.host ?? "their page"} in a new tab. MeeMail never fetches it.`;
  }
}

export function hasMethod(methods: UnsubscribeMethodsView, rung: UnsubscribeRung): boolean {
  return rung === "one_click"
    ? methods.oneClick != null
    : rung === "mailto"
      ? methods.mailto != null
      : methods.link != null;
}

export function availableRungs(methods: UnsubscribeMethodsView): UnsubscribeRung[] {
  return RUNGS.filter((r) => hasMethod(methods, r));
}

/**
 * Rungs the owner may still choose besides the suggested one: the ones further up the ladder
 * (a rung that failed is retried through its work item, not offered again); none while a rung
 * is in flight or once one is done.
 */
export function otherRungs(view: Pick<UnsubscribeView, "methods" | "suggestedRung" | "state">) {
  if (view.state.state === "requested" || view.state.state === "done") return [];
  const after = view.suggestedRung ? RUNGS.indexOf(view.suggestedRung) + 1 : 0;
  return RUNGS.slice(after).filter((r) => hasMethod(view.methods, r));
}

/** Where the sender stands, in words: "Unsubscribed via one-click", "Their server didn't answer". */
export function unsubscribeStateLabel(status: UnsubscribeStatus | null | undefined): string {
  if (!status) return "Not asked";
  switch (status.state) {
    case "none":
      return "Not asked";
    case "requested":
      return status.rung === "link"
        ? "You opened their link"
        : status.rung === "mailto"
          ? "Email approved, sending"
          : "Request on its way";
    case "done":
      return status.rung === "mailto" ? "Asked by email" : "Unsubscribed via one-click";
    case "failed":
      return status.rung === "mailto" ? "The email didn't send" : "Their server refused";
    case "unknown":
      return "Their server didn't answer";
  }
}

/** Whether the sender's row or line should show the ladder at all. */
export function showsLadder(
  sender: Pick<SenderView, "isList" | "unsubscribe">,
  view?: Pick<UnsubscribeView, "methods" | "state"> | null,
): boolean {
  if (sender.isList) return true;
  if (sender.unsubscribe && sender.unsubscribe.state !== "none") return true;
  if (view && (availableRungs(view.methods).length > 0 || view.state.state !== "none")) return true;
  return false;
}

export type LadderTone = "plain" | "note" | "calm";

export function ladderTone(status: UnsubscribeStatus | null | undefined): LadderTone {
  switch (status?.state) {
    case "done":
      return "calm";
    case "failed":
    case "unknown":
      return "note";
    default:
      return "plain";
  }
}

/** The line that follows a rung in flight or its outcome, from the ladder's work item. */
export function ladderDetail(view: Pick<UnsubscribeView, "state" | "work">): string | null {
  const detail = view.state.detail;
  if (detail) return detail;
  const work = view.work;
  if (!work) return null;
  if (work.status === "working") return "Posting the request…";
  if (work.status === "queued") return "Queued; posting shortly.";
  return null;
}

/** After a rung the owner may also screen the sender out; MeeMail never does that on its own. */
export function offersScreenOut(view: Pick<UnsubscribeView, "state" | "screen">): boolean {
  return view.state.state !== "none" && view.screen !== "screened_out";
}
